import { HttpContextToken } from '@angular/common/http';

/**
 * Marks a request as USER INITIATED, so the error interceptor surfaces a permission denial (403) to
 * the user even for GET requests.
 *
 * Background GET requests are deliberately silent on a 403: pages such as the dashboard fire many
 * widget loads at once and those components render their own inline "no permission" state, so
 * toasting each denial would be redundant and noisy. A GET that the user explicitly triggered (an
 * export, a file download, opening a record) has no such inline state, and failing silently leaves
 * the user with no feedback at all.
 *
 * Requests made with ApiRequestService.makeDownloadRequest() set this automatically, since a
 * download is always user initiated. For other GETs, opt in via the
 * `surfacePermissionErrors` argument on makeRequest()/makeRequestObservable().
 */
export const SURFACE_PERMISSION_ERRORS = new HttpContextToken<boolean>(() => false);

/**
 * Marks a request as BACKGROUND, meaning the user did not trigger it (a poll, a focus validation, a
 * badge refresh).
 *
 * A failure to reach the server (status 0) on such a request is never shown to the user, because
 * nothing the user did caused it and there is nothing for them to retry. The error is still
 * rethrown so the caller's own catch logic runs.
 *
 * Affects ONLY the status 0 branch of the error interceptor: 4xx, 401 and 5xx behave as before.
 * Opt in via the `backgroundRequest` argument on makeRequest()/makeRequestObservable().
 */
export const BACKGROUND_REQUEST = new HttpContextToken<boolean>(() => false);
